import React, { useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { BrandButton } from '@/components/brand/Buttons';
import { LogoMark } from '@/components/brand/Logo';
import { useGoogleLogin } from '@react-oauth/google';

/* ---------- field primitives ---------- */
const baseInput =
  'w-full h-11 px-4 rounded-xl bg-cream-50 border border-border placeholder:text-ink-muted/70 text-ink ' +
  'focus:outline-none focus:ring-2 focus:ring-clay-500/40 focus:border-clay-300 transition';

const Field = ({ label, hint, error, required, children }) => (
  <label className="block">
    <span className="block text-[13px] font-medium text-ink-soft mb-1.5">
      {label}{required && <span className="text-clay-600 ml-0.5">*</span>}
    </span>
    {children}
    {error ? (
      <span className="block text-xs text-clay-700 mt-1">{error}</span>
    ) : hint ? (
      <span className="block text-xs text-ink-muted mt-1">{hint}</span>
    ) : null}
  </label>
);

const strengthMeta = {
  weak:   { label: 'A little soft',     bar: 'w-1/4 bg-blush-300' },
  medium: { label: 'Decent',            bar: 'w-2/4 bg-cream-300' },
  strong: { label: 'Strong',            bar: 'w-full bg-sage-400' },
};

/* -------------------------------------------------------------------------- */
/*  AuthModal                                                                  */
/* -------------------------------------------------------------------------- */

const AuthModal = ({ isOpen, onClose, mode = 'signup', onModeChange }) => {
  const [isLogin, setIsLogin] = useState(mode === 'login');
  const [loading, setLoading] = useState(false);
  const [showOTP, setShowOTP] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login, signup, sendOTP, verifyOTP, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '+91 ',
    address: '',
    city: '',
    state: '',
    pincode: '',
    country: '',
  });
  const [passwordStrength, setPasswordStrength] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [otp, setOtp] = useState('');

  /* ---------- helpers ---------- */
  const checkPasswordStrength = (p) => {
    if (!p) return '';
    if (p.length < 6) return 'weak';
    if (p.length >= 8 && /[A-Z]/.test(p) && /[0-9]/.test(p)) return 'strong';
    return 'medium';
  };

  const validateField = (field, value) => {
    const errs = { ...validationErrors };
    if (field === 'email') {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) errs.email = 'That email looks off';
      else delete errs.email;
    } else if (field === 'phone') {
      const digits = value.replace(/[^0-9]/g, '');
      if (digits.length < 12 || digits.length > 13) errs.phone = 'A valid 10-digit number, please';
      else delete errs.phone;
    } else if (field === 'pincode') {
      if (!/^[0-9]{6}$/.test(value)) errs.pincode = '6 digits';
      else delete errs.pincode;
    }
    setValidationErrors(errs);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone' && !value.startsWith('+91 ')) {
      setFormData({ ...formData, phone: '+91 ' });
      return;
    }
    setFormData({ ...formData, [name]: value });
    if (!isLogin) validateField(name, value);
    if (name === 'password') setPasswordStrength(checkPasswordStrength(value));
  };

  const googleLogin = useGoogleLogin({
    flow: 'implicit',
    onSuccess: async (tokenResponse) => {
      setLoading(true);
      try {
        await loginWithGoogle({ access_token: tokenResponse.access_token });
        toast.success('Welcome.');
        onClose();
        navigate('/dashboard');
      } catch (error) {
        toast.error(error.response?.data?.detail || "Couldn't sign in with Google");
      } finally {
        setLoading(false);
      }
    },
    onError: () => toast.error("Couldn't sign in with Google"),
  });

  const handleGoogleLogin = () => googleLogin();

  /* ---------- submit ---------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isLogin && Object.keys(validationErrors).length > 0) return toast.error('A couple of fields need fixing');
    setLoading(true);
    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success('Welcome back.');
        onClose();
        navigate('/dashboard');
      } else {
        await signup(formData);
        toast.success('Your account is ready.');
        await sendOTP(formData.phone);
        setShowOTP(true);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    setLoading(true);
    try {
      await verifyOTP(formData.phone, otp);
      toast.success('Phone verified.');
      setShowOTP(false);
      onClose();
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Code didn\'t match');
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = () => {
    const next = !isLogin;
    setIsLogin(next);
    setValidationErrors({});
    setPasswordStrength('');
    onModeChange?.(next ? 'login' : 'signup');
  };

  /* ---------- render ---------- */

  // --- OTP step ---
  if (showOTP) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[440px] bg-cream-50 border-border/70 rounded-3xl p-0 overflow-hidden">
          <div className="px-8 pt-9 pb-3 text-center">
            <LogoMark size={28} className="text-clay-700 mx-auto mb-5" />
            <h2 className="font-display text-[26px] leading-tight text-ink">
              We've sent you a code.
            </h2>
            <p className="mt-2 text-sm text-ink-soft">
              To <span className="text-ink font-medium">{formData.phone}</span>. Enter it below.
            </p>
          </div>
          <div className="px-8 pb-8 pt-3 space-y-4">
            <Field label="Verification code" required>
              <Input
                inputMode="numeric"
                maxLength={6}
                autoFocus
                placeholder="6 digits"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, ''))}
                className={`${baseInput} text-center tracking-[0.5em] font-display text-xl`}
              />
            </Field>
            <BrandButton
              onClick={handleVerifyOTP}
              disabled={loading || otp.length !== 6}
              loading={loading}
              size="lg"
              className="w-full"
            >
              Verify and continue
            </BrandButton>
            <button
              onClick={() => setShowOTP(false)}
              className="w-full text-center text-sm text-ink-muted hover:text-clay-700 inline-flex items-center justify-center gap-1.5 pt-1"
            >
              <ArrowLeft className="h-3 w-3" /> Back to sign up
            </button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // --- Auth step ---
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[480px] bg-cream-50 border-border/70 rounded-3xl p-0 overflow-hidden max-h-[92vh] overflow-y-auto">
        <div className="px-8 pt-9 pb-2 text-center">
          <LogoMark size={28} className="text-clay-700 mx-auto mb-5" />
          <h2 className="font-display text-[26px] leading-tight text-ink">
            {isLogin ? 'Welcome back.' : (
              <>Begin a basket{' '}
                <span className="italic text-clay-700" style={{ fontVariationSettings: '"opsz" 144, "SOFT" 80' }}>
                  with us.
                </span>
              </>
            )}
          </h2>
          <p className="mt-2 text-sm text-ink-soft">
            {isLogin ? 'Sign in to your registries and gifts.' : 'Free, always — for you and every guest.'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="px-8 pb-8 pt-5 space-y-4">
          {/* Google */}
          <BrandButton
            type="button"
            variant="outline"
            size="lg"
            onClick={handleGoogleLogin}
            disabled={loading}
            className="w-full h-12 border-border bg-cream-50 hover:bg-cream-100"
          >
            <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" aria-hidden>
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </BrandButton>

          <div className="relative my-1">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
            <div className="relative flex justify-center">
              <span className="bg-cream-50 px-3 text-[11px] uppercase tracking-[0.18em] text-ink-muted">
                or with email
              </span>
            </div>
          </div>

          {!isLogin && (
            <Field label="Your name" required>
              <Input name="name" required value={formData.name} onChange={handleChange} placeholder="Aanya Sharma" className={baseInput} />
            </Field>
          )}

          <Field label="Email" required error={validationErrors.email}>
            <Input name="email" type="email" required value={formData.email} onChange={handleChange} placeholder="you@example.com" className={baseInput} />
          </Field>

          <Field
            label="Password"
            required
            hint={!isLogin && !passwordStrength ? 'At least 6 characters. Stronger is kinder to your future self.' : undefined}
          >
            <div className="relative">
              <Input
                name="password"
                type={showPassword ? 'text' : 'password'}
                required
                value={formData.password}
                onChange={handleChange}
                placeholder={isLogin ? 'Your password' : 'Create a password'}
                className={`${baseInput} pr-11`}
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-muted hover:text-ink"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {!isLogin && passwordStrength && (
              <div className="mt-2">
                <div className="h-1 rounded-full bg-cream-200 overflow-hidden">
                  <div className={`h-full rounded-full transition-all ${strengthMeta[passwordStrength].bar}`} />
                </div>
                <p className="text-[11px] text-ink-muted mt-1">{strengthMeta[passwordStrength].label}</p>
              </div>
            )}
          </Field>

          {!isLogin && (
            <>
              <Field label="Phone (for OTP)" required error={validationErrors.phone}>
                <Input name="phone" value={formData.phone} onChange={handleChange} placeholder="+91 9XXXXXXXXX" className={baseInput} />
              </Field>

              <Field label="Address">
                <Input name="address" value={formData.address} onChange={handleChange} placeholder="House, street, area" className={baseInput} />
              </Field>

              <div className="grid grid-cols-2 gap-3">
                <Field label="City">
                  <Input name="city" value={formData.city} onChange={handleChange} placeholder="Bangalore" className={baseInput} />
                </Field>
                <Field label="State">
                  <Input name="state" value={formData.state} onChange={handleChange} placeholder="Karnataka" className={baseInput} />
                </Field>
              </div>

              <Field label="Pincode" error={validationErrors.pincode}>
                <Input name="pincode" inputMode="numeric" maxLength={6} value={formData.pincode} onChange={handleChange} placeholder="560001" className={baseInput} />
              </Field>
            </>
          )}

          <BrandButton
            type="submit"
            size="lg"
            disabled={loading || (!isLogin && Object.keys(validationErrors).length > 0)}
            loading={loading}
            className="w-full mt-2"
          >
            {isLogin ? 'Sign in' : 'Create your account'}
          </BrandButton>

          {!isLogin && (
            <p className="text-[11px] text-ink-muted text-center pt-1 leading-relaxed">
              By continuing you agree to our{' '}
              <button type="button" onClick={() => window.open('/terms', '_blank')} className="underline decoration-clay-300 underline-offset-4 hover:text-clay-700">Terms</button>
              {' '}and{' '}
              <button type="button" onClick={() => window.open('/privacy', '_blank')} className="underline decoration-clay-300 underline-offset-4 hover:text-clay-700">Privacy</button>.
            </p>
          )}

          <p className="text-center text-sm text-ink-soft pt-2">
            {isLogin ? "New here? " : 'Already with us? '}
            <button
              type="button"
              onClick={toggleMode}
              className="text-clay-700 hover:text-clay-800 font-medium underline decoration-clay-300 underline-offset-4"
            >
              {isLogin ? 'Begin a basket' : 'Sign in'}
            </button>
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;
